import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import MemberMembershipService from '../../../../../../services/member_membership_service';
import ConfirmDialog from '../../../../../../components/Common/ConfirmDialog';
import MemberSearch from '../../../../../../components/Common/MemberSearch';
import { useDebounce } from '../../../../../../hooks/useDebounce';
import { useMembers } from '../../../../../../hooks/useMembers';
import { NeoButton, NeoBadge, NeoInput } from '../../../../../../components/Common';

const ActionTransfer = ({ membership, idTenant, user, onRefresh }) => {
    const { members: allMembers } = useMembers();
    const [searchTerm, setSearchTerm] = useState("");
    const [candidates, setCandidates] = useState([]);
    const [targetMember, setTargetMember] = useState(null);
    const [reason, setReason] = useState("");
    const [daysToTransfer, setDaysToTransfer] = useState("");
    const [loading, setLoading] = useState(false);
    
    const debouncedSearchItem = useDebounce(searchTerm, 400);

    // Saldo de dias origem
    const remainingDays = membership?.endDate 
        ? Math.max(0, Math.ceil((new Date(membership.endDate + "T00:00:00").getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
        : 0;

    useEffect(() => {
        // Preenche por padrão tudo que resta
        if (remainingDays > 0) {
            setDaysToTransfer(remainingDays);
        }
    }, [remainingDays]);

    useEffect(() => {
        let isMounted = true;
        const fetchMembers = async () => {
            if (!debouncedSearchItem || debouncedSearchItem.length < 3) {
                if (isMounted) setCandidates([]);
                return;
            }
            try {
                const lower = debouncedSearchItem.toLowerCase();
                const filtered = (allMembers || []).filter(m =>
                    (m.fullName && m.fullName.toLowerCase().includes(lower)) ||
                    (m.cpf && m.cpf.includes(lower)) ||
                    (m.document && String(m.document).includes(lower)) ||
                    (m.memberNumber && String(m.memberNumber).includes(lower))
                );
                
                // Excluir o próprio membro atual da busca!
                const semEleMesmo = filtered.filter(m => m.id !== membership?.idMember);
                
                if (isMounted) setCandidates(semEleMesmo.slice(0, 10)); // max 10
            } catch (error) {
                console.error("Erro na busca de alunos:", error);
            }
        };

        fetchMembers();
        return () => { isMounted = false; };
    }, [debouncedSearchItem, allMembers, membership]);

    const handleTransfer = async () => {
        if (!targetMember) return toast.warning("Selecione um aluno recebedor da transferência.");
        if (daysToTransfer <= 0 || daysToTransfer > remainingDays) {
            return toast.warning(`Informe um número de dias válido, entre 1 e ${remainingDays}.`);
        }
        
        const ok = await ConfirmDialog({
            title: "Transferir Plano?",
            text: `Tem certeza que deseja transferir ${daysToTransfer} dia(s) para: ${targetMember.fullName}? O plano de origem perderá esses dias.`,
            confirmButtonText: "Sim, transferir",
            confirmButtonColor: "#343a40"
        });
        if (!ok) return;

        setLoading(true);
        try {
            await MemberMembershipService.transfer(
                membership.id, 
                idTenant, 
                user, 
                membership, 
                targetMember.id, 
                parseInt(daysToTransfer, 10), 
                reason
            );
            
            toast.success(`Plano transferido para ${targetMember.fullName}.`);
            setTargetMember(null);
            setSearchTerm("");
            setReason("");
            onRefresh?.();
        } catch { 
            toast.error("Erro ao transferir plano."); 
        } finally { 
            setLoading(false); 
        }
    };

    const handleSelectMember = (selected) => {
        setTargetMember(selected);
        setSearchTerm("");
        setCandidates([]);
    };

    return (
        <div className="p-4 border mt-3 bg-white shadow-sm" style={{ borderRadius: '12px', borderColor: 'rgba(0, 0, 0, 0.06)', fontFamily: 'Outfit, sans-serif' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                    <i className="mdi mdi-swap-horizontal-bold fs-4 me-2 text-muted" style={{ color: 'var(--color-brand-secondary) !important' }}></i>
                    <h6 className="fw-bold mb-0 text-uppercase" style={{ letterSpacing: "0.5px", color: 'var(--color-brand-primary)', fontFamily: 'Outfit, sans-serif' }}>
                        Transferir Plano
                    </h6>
                </div>
                <div>
                    <NeoBadge color="accent" className="font-size-12 px-3 py-2">
                        Saldo Origem: {remainingDays} dia(s)
                    </NeoBadge>
                </div>
            </div>

            <p className="text-muted font-size-13 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Transfira o tempo restante de vigência para outro aluno. Se transferir <b>todo o saldo</b>, este contrato será encerrado. A métrica de Churn (cancelamentos) <b>não será inflada</b> por esta ação.
            </p>
            
            <Row className="g-3 align-items-start" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Col md={4}>
                    <div className="font-size-12 fw-bold text-muted mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>Buscar Recebedor *</div>
                    {!targetMember ? (
                        <div style={{ zIndex: 99 }}>
                            <MemberSearch
                                value={searchTerm}
                                onChange={setSearchTerm}
                                candidates={candidates}
                                onSelect={handleSelectMember}
                                placeholder="Digite nome, CPF ou ID do aluno"
                                showNoResults={searchTerm.length >= 3 && candidates.length === 0}
                                noResultsLabel="Nenhum membro encontrado"
                            />
                        </div>
                    ) : (
                        <div className="d-flex align-items-center bg-white py-2 px-3 border shadow-sm" style={{ height: "36px", borderRadius: '8px', borderColor: 'rgba(72, 39, 18, 0.08)' }}>
                            <div className="text-truncate flex-grow-1 font-size-13 fw-bold text-dark">
                                {targetMember.fullName}
                            </div>
                            <i 
                                className="mdi mdi-close-circle text-muted ms-2 cursor-pointer font-size-16"
                                onClick={() => setTargetMember(null)}
                            />
                        </div>
                    )}
                </Col>

                <Col md={2}>
                    <NeoInput
                        label="Qtd Dias *"
                        type="number"
                        min={1}
                        max={remainingDays}
                        value={daysToTransfer}
                        onChange={e => setDaysToTransfer(e.target.value)}
                        size="sm"
                    />
                </Col>

                <Col md={4}>
                    <NeoInput
                        label="Motivo (Opcional)"
                        placeholder="Ex: Doação por viagem..."
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        size="sm"
                    />
                </Col>

                <Col md={2} className="d-flex align-items-end" style={{ height: "62px" }}>
                    <NeoButton
                        variant="primary"
                        onClick={handleTransfer}
                        loading={loading}
                        disabled={!targetMember || !daysToTransfer}
                        size="sm"
                        className="w-100 mb-0"
                        style={{ height: "36px" }}
                    >
                        Confirmar
                    </NeoButton>
                </Col>
            </Row>
        </div>
    );
};

export default ActionTransfer;
